import React, { useEffect } from 'react';
import { useAppActions, useAppState } from '../../context';
import AppPageListItem from './pageListItem';
import { makeStyles, mergeClasses } from '@fluentui/react-components';
import AppPageGroup from './pageGroup';
import { PageReference, RoutingItemStepView, RoutingItemView } from '@gkn-ili/views/src/OrderDocumentation';
import {
  isPageReferenceActivity,
  isPageReferenceActivityGroup,
  isPageReferenceStep,
} from '@gkn-ili/views/dist/OrderDocumentation';

const useStyles = makeStyles({
  root: {
    overflowY: 'hidden',
    '::-webkit-scrollbar': {
      width: '0px',
    },
    height: '100%',
  },
});

function serializePageRef(pageRef: PageReference): string {
  const key = [
    pageRef.routingItemKey,
    pageRef.stepKey,
    isPageReferenceActivityGroup(pageRef) ? pageRef.activityGroupKey : '',
    isPageReferenceActivity(pageRef) ? pageRef.activityKey : '',
  ];
  return key.join('.');
}
function deserializePageRef(pageKey: string): PageReference {
  const splits = pageKey.split('.');
  if (splits.length !== 4) {
    throw new Error('Page key should split into four');
  }

  return {
    routingItemKey: splits[0],
    stepKey: splits[1],
    activityGroupKey: splits[2].length > 0 ? splits[2] : undefined,
    activityKey: splits[3].length > 0 ? splits[3] : undefined,
  };
}

export default function AppContentList(props: React.PropsWithoutRef<{ className: string }>): JSX.Element {
  const appState = useAppState();
  const appActions = useAppActions();
  const styles = useStyles();

  const routingItem = appState.orderView.routing[appState.activePage.routingItemKey];
  const activePageRef = serializePageRef(appState.activePage);

  const refContainer = React.useRef<HTMLDivElement>(null);
  const refPageHeaders = React.useRef<Record<string, HTMLDivElement>>({});

  const [fillerHeight, setFillerHeight] = React.useState(0);
  function computeFillerHeight() {
    if (!refContainer.current) {
      return;
    }
    const itemHeight = 64;
    const height = (refContainer.current.clientHeight - itemHeight) / 2;

    setFillerHeight(height);
  }

  function scrollToActivePage(activePage: PageReference | string, fillerHeight: number) {
    if (typeof activePage === 'object') {
      activePage = serializePageRef(activePage);
    }
    if (refContainer.current && refPageHeaders.current) {
      const top = refPageHeaders.current[activePage].offsetTop - refContainer.current.offsetTop - fillerHeight;
      refContainer.current.scrollTo({ top, left: 0, behavior: 'smooth' });
    }
  }
  useEffect(() => {
    scrollToActivePage(activePageRef, fillerHeight);
  }, [activePageRef, fillerHeight]);

  useEffect(() => computeFillerHeight(), []);

  function onScroll(ev: WheelEvent): void {
    const locX = ev.pageX;
    const locY = ev.pageY;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wheelDeltaY = (ev as any).wheelDeltaY;
    if (refContainer.current) {
      const containerTop = refContainer.current.offsetTop;
      if (
        containerTop < locY &&
        containerTop + refContainer.current.offsetHeight > locY &&
        refContainer.current.offsetLeft < locX &&
        refContainer.current.offsetLeft + refContainer.current.offsetWidth > locX
      ) {
        const maxScroll = 20;
        const nextScroll = refContainer.current.scrollTop - Math.max(Math.min(wheelDeltaY, maxScroll), -maxScroll);
        refContainer.current.scrollTo({ top: nextScroll, left: 0, behavior: 'auto' });
        const selectedPage = Object.entries(refPageHeaders.current).reduce<[string, number]>(
          (result: [string, number], current) => {
            const [, div] = current;
            const parentIsExpanded = div.parentElement?.ariaExpanded ?? 'true';
            // if its parent is not visible, because it is not expanded, skip the element
            if (parentIsExpanded === 'false') {
              return result;
            }
            // Compute thow much it is below the target scroll position
            const diffTarget = div.offsetTop - containerTop - fillerHeight - nextScroll;
            // If this item has a smaller target than are best this one should be it
            if (Math.abs(diffTarget) < Math.abs(result[1])) {
              return [current[0], diffTarget];
            }
            return result;
          },
          [Object.keys(refPageHeaders.current)[0], 320000],
        );
        if (selectedPage[0] !== activePageRef) {
          appActions.updateActivePage(deserializePageRef(selectedPage[0]));
        }
      }
    }
  }
  useEffect(() => {
    document.addEventListener('wheel', onScroll);
    return () => document.removeEventListener('wheel', onScroll);
  });

  function createSaveRootRef(path: string): (element: HTMLDivElement | null) => void {
    return (element: HTMLDivElement | null) => {
      if (refPageHeaders.current === null) {
        refPageHeaders.current = {};
      }
      if (element) {
        refPageHeaders.current[path] = element;
      }
    };
  }

  function groupRoutingStep(key: string, step: RoutingItemStepView): React.ReactNode {
    return (
      <AppPageGroup
        key={key}
        name={step.name}
        forceExpanded={appState.activePage.stepKey === key}
        onGroupClick={() =>
          appActions.updateActivePage({
            ...appState.activePage,
            stepKey: key,
            activityGroupKey: undefined,
            activityKey: undefined,
          })
        }
        selected={key === appState.activePage.stepKey && isPageReferenceStep(appState.activePage)}
        rootRef={createSaveRootRef(
          serializePageRef({ ...appState.activePage, stepKey: key, activityGroupKey: undefined }),
        )}
      >
        {Object.entries(step.activityGroups).map(([activityGroupKey, value]) => {
          const ssKey = activityGroupKey;
          return (
            <AppPageListItem
              key={ssKey}
              name={value.name}
              selected={
                key === appState.activePage.stepKey &&
                'activityGroupKey' in appState.activePage &&
                ssKey === appState.activePage.activityGroupKey
              }
              onClick={() =>
                appActions.updateActivePage({
                  ...appState.activePage,
                  stepKey: key,
                  activityGroupKey: ssKey,
                  activityKey: undefined,
                })
              }
              rootRef={createSaveRootRef(
                serializePageRef({ ...appState.activePage, stepKey: key, activityGroupKey: ssKey }),
              )}
            />
          );
        })}
      </AppPageGroup>
    );
  }
  function groupRoutingItem(item: RoutingItemView): React.ReactNode {
    return <>{Object.entries(item.steps).map(([key, value]) => groupRoutingStep(key, value))}</>;
  }

  const content = groupRoutingItem(routingItem);
  return (
    <div className={mergeClasses(props.className, styles.root)} ref={refContainer} onResize={computeFillerHeight}>
      <div style={{ height: fillerHeight }}></div>
      {content}
      <div style={{ height: fillerHeight }}></div>
    </div>
  );
}
